import React, { useState } from "react";
import styled from "styled-components";
import ResponsiveBox from "./ResponsiveBox";
import CommentBox from "./CommentBox";
import { X } from "react-feather";
import { mediaQueries } from "../design-system/tokens/breakpoints";

const ChatSectionWrapper = styled(ResponsiveBox)<{ $isChatOpen: boolean }>`
  position: absolute;
  top: 70px; // Adjust based on your header height
  right: 10px;
  width: calc(25% - 25px);
  padding: 10px;
  height: calc(100vh - 88px); // Adjust based on your header height
  transform: translateX(${({ $isChatOpen }) => ($isChatOpen ? "0" : "100%")});
  opacity: ${({ $isChatOpen }) => ($isChatOpen ? "1" : "0")};
  visibility: ${({ $isChatOpen }) => ($isChatOpen ? "visible" : "hidden")};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  pointer-events: ${({ $isChatOpen }) => ($isChatOpen ? "auto" : "none")};
  display: ${({ $isChatOpen }) => ($isChatOpen ? "block" : "none")};

  &.open {
    transform: translateX(0);
  }

  &::before {
    height: 0px;
  }

  /* Phone: a full-width bottom sheet sliding up (300ms), instead of the 25% column. */
  ${mediaQueries.down.sm} {
    top: auto;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 80vh;
    height: 80dvh;
    box-sizing: border-box;
    border-radius: 1rem 1rem 0 0;
    transform: translateY(${({ $isChatOpen }) => ($isChatOpen ? "0" : "100%")});
    &.open {
      transform: translateY(0);
    }
    @media (prefers-reduced-motion: reduce) {
      transition-duration: 0.01ms;
    }
  }
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ChatTitle = styled.div`
  margin: 0;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
`;

const CommentsContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  //max-height: calc(100% - 40px); // Adjust based on your header height
`;

interface ChatSectionProps {
  isChatOpen: boolean;
  children?: React.ReactNode;
  onClose: () => void;
}

const ChatSection: React.FC<ChatSectionProps> = ({ isChatOpen, onClose }) => {
  const [selectedComment, setSelectedComment] = useState<number | null>(null);

  const comments = [
    {
      id: 1,
      content:
        "@Andy Can you clarify if the market share growth rate of 3.2% (NBRx) and 2.6% (TRx) are for the same regions as the revenue performance chart below?",
      user: { name: "John Doe", timestamp: "2 hours ago", color: "#4CAF50" },
      responses: [
        {
          id: 101,
          content:
            "@John Yes, the growth rates are for the same regions. The chart shows a consolidated view across all our key markets.",
          user: {
            name: "Andy Smith",
            timestamp: "1 hour ago",
            color: "#2196F3",
          },
        },
        {
          id: 102,
          content:
            "Thanks for the clarification, @Andy. That helps put things in perspective.",
          user: {
            name: "John Doe",
            timestamp: "45 minutes ago",
            color: "#4CAF50",
          },
        },
      ],
    },
    {
      id: 2,
      content:
        "@Nick Can we get a breakdown of patient adherence by demographics, like age group and insurance type? This would help us understand if certain segments are at higher risk of non-adherence.",
      user: { name: "Jane Smith", timestamp: "1 hour ago", color: "#f44336" },
      responses: [
        {
          id: 201,
          content:
            "@Jane That's a great idea. I'll work on pulling that data. @Barry, can you help with the insurance type breakdown?",
          user: {
            name: "Nick Johnson",
            timestamp: "50 minutes ago",
            color: "#9C27B0",
          },
        },
        {
          id: 202,
          content:
            "@Nick Sure, I can help with the insurance type data. I'll have it ready by tomorrow morning.",
          user: {
            name: "Barry White",
            timestamp: "40 minutes ago",
            color: "#FF9800",
          },
        },
      ],
    },
    {
      id: 3,
      content:
        "I've noticed our market share in the Midwest region has been declining. @Andy, do we have any specific strategies in place to address this?",
      user: { name: "Emma Lee", timestamp: "3 hours ago", color: "#009688" },
      responses: [
        {
          id: 301,
          content:
            "@Emma Yes, we're aware of the situation in the Midwest. We're planning to launch a targeted campaign next month to boost our presence there.",
          user: {
            name: "Andy Smith",
            timestamp: "2 hours ago",
            color: "#2196F3",
          },
        },
        {
          id: 302,
          content:
            "@Andy That sounds promising. Can you share more details about the campaign? I'd like to align our sales efforts with it.",
          user: { name: "Emma Lee", timestamp: "1 hour ago", color: "#009688" },
        },
        {
          id: 303,
          content:
            "@Emma Absolutely. I'll set up a meeting for us to go through the campaign details and discuss how we can integrate our sales strategy.",
          user: {
            name: "Andy Smith",
            timestamp: "30 minutes ago",
            color: "#2196F3",
          },
        },
      ],
    },
  ];

  return (
    <ChatSectionWrapper
      $isChatOpen={isChatOpen}
      className={isChatOpen ? "open" : ""}
    >
      <ChatHeader>
        <ChatTitle>Comments</ChatTitle>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
      </ChatHeader>
      <CommentsContainer>
        {comments.map((comment, index) => (
          <CommentBox
            key={comment.id}
            content={comment.content}
            responses={comment.responses.map((response) => ({
              content: response.content,
              onSelect: () => {},
              isSelected: false,
              user: {
                name: response.user.name,
                timestamp: response.user.timestamp,
                color: response.user.color,
              },
            }))}
            onSelect={() => setSelectedComment(index)}
            isSelected={selectedComment === index}
            user={{
              name: comment.user.name,
              timestamp: comment.user.timestamp,
              color: comment.user.color,
            }}
          />
        ))}
      </CommentsContainer>
    </ChatSectionWrapper>
  );
};

export default ChatSection;
